
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// module components
import ContractForm from '@/modules/ContractsModule/components/ContractForm/index.vue'
import AttachDocumentForm from '@/modules/ContractsModule/components/AttachDocumentForm/index.vue'

// types
import { ContractT } from '@/core/types/contracts.types'
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { contracts } from '@/core/realisations/contracts.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// constants
import { CONTRACT_STATUSES } from '@/core/constants/contracts.constants'
import { ArchiveContractsColumns } from '@/core/constants/columns/Contracts.columns'

// classes
import { DataState } from '@/core/classes/DataState.class'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

const MAX_NAME_LENGTH_IN_TABLE = 12

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
        'failed-fetch': FailedFetch,
        'contract-form': ContractForm,
        'attach-document-form': AttachDocumentForm,
        Cheap,
        TheTable,
        TableScrollWrapper,
    },
    setup() {
        const state = reactive({
            contracts: new DataState<ContractT>({}),
            is_attach: false,
            is_attach_loading: false,
            contract_id_for_attach: null as number | null,
            is_add_contract: false,
        })

        const filters_state = reactive({
            limit: 50,
            //
            number: null as string | null,
            bin: null as string | null,
            original_name: null as string | null,
            company_id: null as number | null,
            status_ids: [] as number[],
            subdivision_id: null as number | null,
        })

        const data_state = reactive({
            subdivisions: new DataState<any>({}),
            companies: new DataState<CompanyT>({}),
        })

        const loadSubdivisions = async () => {
            try {
                data_state.subdivisions.is_loading = true
                const response = await companies.getSubdivisions({ page: 1 })
                data_state.subdivisions.list = response.data

                data_state.subdivisions.is_failed = false
                data_state.subdivisions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.subdivisions.failure.catchError(error)

                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_failed = true
                }
            } finally {
                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_loading = false
                }
            }
        }

        //

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const params = { page, search, limit: 9999, isExtended: 0, getAll: 1 }
                const response = await companies.getList(params)
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        const loadArchiveContracts = async (page: number) => {
            state.contracts.pagination.current_page = page

            // filters
            const params = {
                page,
                number: filters_state.number as string,
                bin: filters_state.bin as string,
                original_name: filters_state.original_name as string,
                company_id: filters_state.company_id as number,
                status_ids: filters_state.status_ids as number[],
                subdivision_id: filters_state.subdivision_id as number,
                limit: filters_state.limit as number,
            }

            try {
                state.contracts.is_loading = true

                const response = await contracts.getArchiveList(params)

                state.contracts.list = response.data
                state.contracts.pagination.total_pages = response.meta.last_page
                state.contracts.pagination.total_items = response.meta.total

                state.contracts.is_failed = false
                state.contracts.failure.error_state.is_cancel = false
            } catch (error) {
                state.contracts.failure.catchError(error)

                if (!state.contracts.failure.error_state.is_cancel) {
                    state.contracts.is_failed = true
                }
            } finally {
                if (!state.contracts.failure.error_state.is_cancel) {
                    state.contracts.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadArchiveContracts(state.contracts.pagination.current_page)
        }

        onBeforeMount(() => {
            loadCompanies(1)
            loadSubdivisions()
            loadArchiveContracts(1)
        })

        const resetFilters = () => {
            filters_state.number = null
            filters_state.bin = null
            filters_state.original_name = null
            filters_state.company_id = null
            filters_state.status_ids = []
            filters_state.subdivision_id = null

            loadArchiveContracts(1)
        }

        const startAttach = (contract_id: number) => {
            state.contract_id_for_attach = contract_id
            state.is_attach = true
        }

        const cancelAttach = () => {
            state.contract_id_for_attach = null
            state.is_attach = false
        }

        const attachDocument = async (form_data: FormData) => {
            if (!state.contract_id_for_attach) return

            try {
                state.is_attach_loading = true
                await contracts.attachDocument(form_data)
                await loadArchiveContracts(state.contracts.pagination.current_page)
                cancelAttach()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_attach_loading = false
            }
        }

        const onContractAdded = () => {
            state.is_add_contract = false
            loadArchiveContracts(state.contracts.pagination.current_page)
        }

        const cutName = (name: string): string => {
            if (name && name.length > MAX_NAME_LENGTH_IN_TABLE) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, MAX_NAME_LENGTH_IN_TABLE - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const exportExcel = async () => {
            try {
                state.contracts.is_loading = true

                const response = await contracts.exportExcel(filters_state)

                const download_url = window.URL.createObjectURL(response)
                const link = document.createElement('a')
                link.href = download_url
                link.target = '_blank'
                link.setAttribute('download', `Договоры`)
                document.body.appendChild(link)
                link.click()
                link.remove()
            } catch (e) {
                console.log(e)
            } finally {
                state.contracts.is_loading = false
            }
        }

        const getCheckOrTimesIcon = (is_file: boolean) => {
            return `fas ${is_file ? 'fa-check text-green-500 mr-4' : 'fa-times text-red-500 mr-4'}`
        }

        return {
            ArchiveContractsColumns,
            CONTRACT_STATUSES,
            state,
            filters_state,
            loadSubdivisions,
            getCheckOrTimesIcon,
            loadArchiveContracts,
            resetFilters,
            startAttach,
            cancelAttach,
            attachDocument,
            onContractAdded,
            data_state,
            loadCompanies,
            searchCompanies,
            unshiftFindedCompany,
            cutName,
            exportExcel,
            onLimitChange,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
        }
    },
})
